import { createContext, useContext, useState, ReactNode, useEffect, useCallback, useRef } from 'react';
import { useUser } from './user-context';
import { onApiCall } from '../../api/client';

export type LogLevel = 'info' | 'warning' | 'error' | 'debug' | 'critical';
export type LogCategory =
  | 'auth'
  | 'orders'
  | 'reservations'
  | 'inventory'
  | 'billing'
  | 'users'
  | 'menu'
  | 'tables'
  | 'reports'
  | 'system'
  | 'navigation'
  | 'purchases'
  | 'api';

export interface LogEntry {
  id: string;
  timestamp: Date;
  level: LogLevel;
  category: LogCategory;
  action: string;
  description: string;
  userId?: string;
  userName?: string;
  userRole?: string;
  branch?: string;
  metadata?: Record<string, any>;
  stackTrace?: string;
  userAgent?: string;
  ipAddress?: string;
  sessionId?: string;
  duration?: number;
  previousState?: any;
  newState?: any;
}

interface LoggingContextType {
  logs: LogEntry[];
  debugMode: boolean;
  setDebugMode: (enabled: boolean, hours?: number) => void;
  debugModeExpiry: Date | null;
  log: (
    level: LogLevel,
    category: LogCategory,
    action: string,
    description: string,
    metadata?: Record<string, any>
  ) => void;
  clearLogs: () => void;
  exportLogs: () => void;
  getLogStats: () => {
    total: number;
    byLevel: Record<LogLevel, number>;
    byCategory: Record<LogCategory, number>;
    errors: number;
    warnings: number;
  };
}

const LoggingContext = createContext<LoggingContextType | undefined>(undefined);

const SESSION_ID = `sess_${Math.random().toString(36).substring(7)}_${Date.now()}`;
const SIMULATED_IP = `192.168.1.${Math.floor(Math.random() * 255)}`;

const STORAGE_KEY = 'salonika_logs_v2';
const LOG_TTL_MS = 72 * 60 * 60 * 1000; // 72 horas
const TRUNCATE_LEN = 300;
const MAX_LOGS_NORMAL = 1500;
const MAX_LOGS_DEBUG = 5000;

// =============================================================================
// Helpers
// =============================================================================
function truncate(value: unknown, max = TRUNCATE_LEN): { preview: string; truncated: boolean; fullLength: number } {
  if (value === undefined || value === null) {
    return { preview: '', truncated: false, fullLength: 0 };
  }
  let str: string;
  if (typeof value === 'string') {
    str = value;
  } else {
    try { str = JSON.stringify(value); } catch { str = String(value); }
  }
  if (str.length <= max) return { preview: str, truncated: false, fullLength: str.length };
  return { preview: str.slice(0, max) + '…', truncated: true, fullLength: str.length };
}

function loadFromStorage(): LogEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as LogEntry[];
    // Rehidratar fechas + filtrar expirados (TTL 72h) + sanear objetos en action/description
    // (logs viejos antes del blindaje pueden tener objetos donde se esperaba string).
    const now = Date.now();
    const toStr = (v: any) => typeof v === 'string' ? v
      : v == null ? ''
      : (() => { try { return JSON.stringify(v); } catch { return '[object]'; } })();
    return parsed
      .map(l => ({
        ...l,
        timestamp: new Date(l.timestamp),
        action: toStr(l.action),
        description: toStr(l.description),
        branch: l.branch != null ? toStr(l.branch) : undefined,
        userName: l.userName != null ? toStr(l.userName) : undefined,
        userId: l.userId != null ? toStr(l.userId) : undefined,
      }))
      .filter(l => now - l.timestamp.getTime() < LOG_TTL_MS);
  } catch {
    return [];
  }
}

function saveToStorage(logs: LogEntry[]) {
  try {
    // Sólo persistimos los últimos MAX_LOGS_NORMAL para no inflar localStorage
    const slim = logs.slice(0, MAX_LOGS_NORMAL);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(slim));
  } catch {
    /* quota excedida — ignorar */
  }
}

export function LoggingProvider({ children }: { children: ReactNode }) {
  const { user } = useUser();
  const [logs, setLogs] = useState<LogEntry[]>(() => loadFromStorage());
  const [debugMode, setDebugModeState] = useState(false);
  const [debugModeExpiry, setDebugModeExpiry] = useState<Date | null>(null);

  // userRef para evitar re-suscribir el listener de API en cada cambio de user
  const userRef = useRef(user);
  useEffect(() => { userRef.current = user; }, [user]);
  const debugRef = useRef(debugMode);
  useEffect(() => { debugRef.current = debugMode; }, [debugMode]);

  // ---------------------------------------------------------------------------
  // Auto-limpieza por TTL (72 horas)
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const sweep = () => {
      const now = Date.now();
      setLogs(prev => {
        const fresh = prev.filter(l => now - new Date(l.timestamp).getTime() < LOG_TTL_MS);
        if (fresh.length !== prev.length) {
          console.log(`[Logging] Eliminados ${prev.length - fresh.length} logs > 72h`);
        }
        return fresh;
      });
    };
    sweep();
    const interval = setInterval(sweep, 5 * 60 * 1000); // cada 5 min
    return () => clearInterval(interval);
  }, []);

  // ---------------------------------------------------------------------------
  // Persistencia en localStorage (debounced)
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const t = setTimeout(() => saveToStorage(logs), 500);
    return () => clearTimeout(t);
  }, [logs]);

  // ---------------------------------------------------------------------------
  // Expiración del modo debug
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (debugMode && debugModeExpiry) {
      const checkExpiry = setInterval(() => {
        if (new Date() >= debugModeExpiry) {
          setDebugModeState(false);
          setDebugModeExpiry(null);
        }
      }, 1000);
      return () => clearInterval(checkExpiry);
    }
  }, [debugMode, debugModeExpiry]);

  const setDebugMode = useCallback((enabled: boolean, hours: number = 2) => {
    setDebugModeState(enabled);
    if (enabled) {
      const expiry = new Date();
      expiry.setHours(expiry.getHours() + hours);
      setDebugModeExpiry(expiry);
    } else {
      setDebugModeExpiry(null);
    }
  }, []);

  // ---------------------------------------------------------------------------
  // Función principal de log
  // ---------------------------------------------------------------------------
  const log = useCallback((
    level: LogLevel,
    category: LogCategory,
    action: string,
    description: string,
    metadata?: Record<string, any>
  ) => {
    if (level === 'debug' && !debugRef.current) return;

    // Blindaje: si algún caller pasa un objeto en action/description (ej. un
    // Supplier o Purchase entero), lo serializamos antes de guardarlo para que
    // el render en DeveloperLogs no rompa con "Objects are not valid as a React child".
    const safeAction = typeof action === 'string' ? action :
      (action == null ? '' : (() => { try { return JSON.stringify(action); } catch { return '[object]'; } })());
    const safeDesc = typeof description === 'string' ? description :
      (description == null ? '' : (() => { try { return JSON.stringify(description); } catch { return '[object]'; } })());

    const u = userRef.current;
    const entry: LogEntry = {
      id: `log_${Date.now()}_${Math.random().toString(36).substring(7)}`,
      timestamp: new Date(),
      level,
      category,
      action: safeAction,
      description: safeDesc,
      userId: u?.id,
      userName: u?.name,
      userRole: u?.role,
      branch: u?.branch,
      metadata,
      userAgent: navigator.userAgent,
      ipAddress: SIMULATED_IP,
      sessionId: SESSION_ID,
    };

    if (level === 'error' || level === 'critical') {
      try { throw new Error('Stack trace'); } catch (e: any) {
        entry.stackTrace = e.stack;
      }
    }

    if (debugRef.current) {
      entry.metadata = {
        ...entry.metadata,
        debugMode: true,
        memory: (performance as any).memory ? {
          usedJSHeapSize: (performance as any).memory.usedJSHeapSize,
          totalJSHeapSize: (performance as any).memory.totalJSHeapSize,
        } : undefined,
        screen: { width: window.screen.width, height: window.screen.height },
        viewport: { width: window.innerWidth, height: window.innerHeight },
      };
    }

    setLogs(prev => {
      const next = [entry, ...prev];
      const maxLogs = debugRef.current ? MAX_LOGS_DEBUG : MAX_LOGS_NORMAL;
      return next.slice(0, maxLogs);
    });

    if (level === 'critical' || level === 'error') {
      console.error(`[${level.toUpperCase()}] [${category}] ${action}: ${description}`, metadata);
    }
  }, []);

  // ---------------------------------------------------------------------------
  // Subscriber al bus de API → registrar cada llamada al backend automáticamente
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const unsubscribe = onApiCall((e) => {
      const level: LogLevel =
        e.status === 0 || e.status >= 500 ? 'error' :
        e.status >= 400 ? 'warning' : 'info';

      const action = `API_${e.method}`;
      const description = e.errorMessage
        ? `${e.method} ${e.path} → ${e.status || 'NETWORK_ERROR'} · ${e.errorMessage}`
        : `${e.method} ${e.path} → ${e.status} (${e.durationMs.toFixed(0)}ms)`;

      const req = truncate(e.requestBody);
      const resp = truncate(e.responseBody);

      log(level, 'api', action, description, {
        method: e.method,
        path: e.path,
        status: e.status,
        ok: e.ok,
        durationMs: Math.round(e.durationMs),
        requestPreview: req.preview,
        requestTruncated: req.truncated,
        requestLength: req.fullLength,
        responsePreview: resp.preview,
        responseTruncated: resp.truncated,
        responseLength: resp.fullLength,
        errorMessage: e.errorMessage,
      });
    });
    return unsubscribe;
  }, [log]);

  const clearLogs = useCallback(() => {
    setLogs([]);
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
    log('warning', 'system', 'LOGS_CLEARED', 'Todos los logs fueron eliminados manualmente');
  }, [log]);

  const exportLogs = useCallback(() => {
    const dataStr = JSON.stringify(logs, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `salonika_logs_${new Date().toISOString()}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    log('info', 'system', 'LOGS_EXPORTED', `Exportados ${logs.length} logs a archivo JSON`);
  }, [logs, log]);

  const getLogStats = useCallback(() => {
    const byLevel: Record<LogLevel, number> = {
      info: 0, warning: 0, error: 0, debug: 0, critical: 0,
    };
    const byCategory: Record<LogCategory, number> = {
      auth: 0, orders: 0, reservations: 0, inventory: 0, billing: 0,
      users: 0, menu: 0, tables: 0, reports: 0, system: 0,
      navigation: 0, purchases: 0, api: 0,
    };
    logs.forEach(l => {
      if (!l) return;
      // Defensa: si llega un log con level/category fuera del enum (ej. por
      // versiones viejas en localStorage), no rompemos la vista.
      if (l.level && byLevel[l.level] !== undefined) byLevel[l.level]++;
      if (l.category && byCategory[l.category] !== undefined) byCategory[l.category]++;
    });
    return {
      total: logs.length,
      byLevel,
      byCategory,
      errors: byLevel.error + byLevel.critical,
      warnings: byLevel.warning,
    };
  }, [logs]);

  return (
    <LoggingContext.Provider value={{
      logs,
      debugMode,
      setDebugMode,
      debugModeExpiry,
      log,
      clearLogs,
      exportLogs,
      getLogStats,
    }}>
      {children}
    </LoggingContext.Provider>
  );
}

export function useLogging() {
  const context = useContext(LoggingContext);
  if (context === undefined) {
    throw new Error('useLogging must be used within a LoggingProvider');
  }
  return context;
}

export function useActionLogger() {
  const { log } = useLogging();
  return { logAction: log };
}
